import React, { useState, useEffect } from 'react';
import { Search, Bell, Mail, Briefcase, AlertCircle, Ticket, FileText, Clock } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { logoutUser } from '../../../actions/authAction'; // ensure token is removed
import { useNavigate } from 'react-router-dom';

const INITIAL_NOTIFICATIONS = [
  {
    id: 1,
    icon: Briefcase,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    title: 'New merchant registration pending',
    description: 'Horton Plains National Park submitted for review',
    time: '12 minutes ago',
    read: false,
  },
  {
    id: 2,
    icon: AlertCircle,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-500',
    title: 'Tour guide license expiring soon',
    description: "Priya Fernando's license expires Oct 02, 2026",
    time: '1 hour ago',
    read: false,
  },
  {
    id: 3,
    icon: Ticket,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    title: 'New booking received',
    description: '4 tickets booked for Sigiriya Rock Fortress',
    time: '3 hours ago',
    read: true,
  },
  {
    id: 4,
    icon: FileText,
    iconBg: 'bg-brand-blue/10',
    iconColor: 'text-brand-blue',
    title: 'Custom page published',
    description: '"Privacy Policy" is now live on the site',
    time: 'Yesterday',
    read: true,
  },
  {
    id: 5,
    icon: Clock,
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-500',
    title: 'Scheduled maintenance',
    description: 'Portal will be briefly unavailable on Sep 5, 2026',
    time: '2 days ago',
    read: true,
  },
];

const TravelDashboardHeader = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState(INITIAL_NOTIFICATIONS);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [mobileMenuOpen]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest('.profile-container')) {
        setProfileDropdownOpen(false);
      }
      if (notificationsOpen && !event.target.closest('.notifications-container')) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen, notificationsOpen]);

  const handleMarkAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleLogout = () => {
    dispatch(logoutUser()); // remove token and dispatch logout
    setProfileDropdownOpen(false);
    navigate('/login'); // redirect to login page after logout
  };

  return (
    <header className="bg-white border-b border-slate-200 w-full relative z-50">
      <div className="h-12 md:h-16 w-full flex items-center justify-end px-4 md:px-6 lg:px-8">
        <div className="flex items-center space-x-4 lg:space-x-6">
          {/* Search Bar */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search destinations..."
              className="w-48 lg:w-64 h-9 px-10 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <div className="absolute left-3 top-2 text-slate-400">
              <Search size={16} />
            </div>
          </div>

          {/* Notifications */}
          <div className="relative notifications-container">
            <div
              className="flex items-center justify-center w-9 h-9 border rounded-full cursor-pointer bg-slate-50 border-slate-200 hover:bg-slate-100"
              onClick={() => setNotificationsOpen(!notificationsOpen)}
            >
              <Bell size={18} className="text-slate-500" />
            </div>
            {unreadCount > 0 && (
              <div className="absolute flex items-center justify-center w-4 h-4 text-xs text-white bg-red-500 rounded-full -top-1 -right-1">
                {unreadCount}
              </div>
            )}

            {notificationsOpen && (
              <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border shadow-xl rounded-2xl border-slate-100 w-96">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                  <h3 className="text-base font-bold text-slate-900">Notifications</h3>
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-sm font-medium text-brand-blue hover:text-brand-blue-dark"
                  >
                    Mark all as read
                  </button>
                </div>

                <div className="overflow-y-auto max-h-96">
                  {notifications.map((notification) => {
                    const Icon = notification.icon;
                    return (
                      <div
                        key={notification.id}
                        className="flex items-start gap-3 px-5 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                      >
                        <div className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${notification.iconBg}`}>
                          <Icon size={18} className={notification.iconColor} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{notification.title}</p>
                          <p className="mt-0.5 text-sm text-slate-500">{notification.description}</p>
                          <p className="mt-1 text-xs text-slate-400">{notification.time}</p>
                        </div>
                        {!notification.read && (
                          <span className="w-2 h-2 mt-1 rounded-full bg-brand-blue flex-shrink-0" />
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  onClick={() => setNotificationsOpen(false)}
                  className="w-full py-3.5 text-sm font-semibold text-brand-blue hover:bg-slate-50 border-t border-slate-100"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>

          {/* Messages */}
          <div className="relative">
            <div className="h-9 w-9 bg-slate-50 border border-slate-200 rounded-full flex items-center justify-center hover:bg-slate-100 cursor-pointer">
              <Mail size={18} className="text-slate-500" />
            </div>
            <div className="absolute -top-1 -right-1 bg-amber-500 text-white h-4 w-4 rounded-full flex items-center justify-center text-xs">
              2
            </div>
          </div>

          {/* User Profile */}
          <div className="relative profile-container">
            <div
              className="h-10 w-10 bg-blue-800 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors"
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
            >
              <span className="text-white text-sm font-medium">KA</span>
            </div>
            {profileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-md shadow-lg z-50">
                <button
                  className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                  onClick={() => {
                    setProfileDropdownOpen(false);
                    navigate('/Profile');
                  }}
                >
                  View Profile
                </button>
                <button
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-100"
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default TravelDashboardHeader;
